import {isNull, isUndefined} from 'lodash';
import {combineLatest, EMPTY, fromEvent, merge, Observable, of, timer} from 'rxjs';
import {debounce, distinctUntilChanged, first, map, mapTo, skip, startWith, switchMap} from 'rxjs/operators';
import {Key} from 'ts-key-enum';

/*
 * Constants.
 */

const blurGraceTimeoutInMs = 100;

export enum FormattingPopoverInteractionStatesEnum {
  /** The editor and popover are both blurred. */
  BLURRED = 'BLURRED',
  /** The editor is focused. */
  EDITOR_FOCUSED = 'EDITOR_FOCUSED',
  /** The user is typing. */
  EDITOR_TYPING = 'EDITOR_TYPING',
  /** The user is changing the selection with the keyboard. */
  EDITOR_SELECTING_WITH_KEYBOARD = 'EDITOR_SELECTING_WITH_KEYBOARD',
  /** The user is dragging a selection with their mouse. */
  EDITOR_SELECTING_WITH_MOUSE = 'EDITOR_SELECTING_WITH_MOUSE',
  /** The popover is focused (contains an element that is focused). */
  POPOVER_FOCUSED = 'POPOVER_FOCUSED',
  /** The user is hovering their mouse over the popover (regardless of focus). */
  POPOVER_HOVERING = 'POPOVER_HOVERING',
}

enum FormattingPopoverElementsEnum {
  EDITOR = 'EDITOR',
  POPOVER = 'POPOVER',
}

/*
 * API.
 */

/** Emits the "interaction state" when subscribed to (assuming the input observables do) and when it changes. */
export function fromFormattingPopoverInteractionState(
  editorElement$: Observable<HTMLElement | null>,
  popoverContentElement$: Observable<HTMLElement | null>,
): Observable<FormattingPopoverInteractionStatesEnum> {
  const activeElement$ = fromActiveFormattingPopoverElement(editorElement$, popoverContentElement$);
  const editorInteractionState$ = fromEditorInteractionState(editorElement$);
  const popoverInteractionState$ = fromPopoverInteractionState(popoverContentElement$);

  return combineLatest([
    activeElement$,
    // These do not emit when subscribed to, so assume we start with no interaction to unblock combineLatest().
    editorInteractionState$.pipe(startWith(undefined)),
    popoverInteractionState$.pipe(startWith(undefined)),
  ]).pipe(
    map(([activeElement, editorInteractionState, popoverInteractionState]) => {
      switch (activeElement) {
        case FormattingPopoverElementsEnum.EDITOR:
          // Even when the editor is focused, the user may still be interacting with the popover (such as by hovering).
          return (
            popoverInteractionState ??
            editorInteractionState ??
            FormattingPopoverInteractionStatesEnum.EDITOR_FOCUSED
          );

        case FormattingPopoverElementsEnum.POPOVER:
          return popoverInteractionState ?? FormattingPopoverInteractionStatesEnum.POPOVER_FOCUSED;

        default:
          return FormattingPopoverInteractionStatesEnum.BLURRED;
      }
    }),
  );
}

/*
 * Helpers.
 */

/** Emits the active element, when subscribed to (assuming the input observables do) and when it changes. */
export function fromActiveFormattingPopoverElement(
  editorElement$: Observable<HTMLElement | null>,
  popoverContentElement$: Observable<HTMLElement | null>,
): Observable<FormattingPopoverElementsEnum | null> {
  return combineLatest([
    editorElement$.pipe(switchMap(fromIsActive)),
    popoverContentElement$.pipe(switchMap(fromIsActive)),
  ]).pipe(
    map(([isEditorActive, isPopoverActive]) => {
      if (isEditorActive) {
        return FormattingPopoverElementsEnum.EDITOR;
      }

      if (isPopoverActive) {
        return FormattingPopoverElementsEnum.POPOVER;
      }

      // Neither element has/contains focus.
      return null;
    }),
    // The browser auto-focuses newly rendered elements after a delay, so wait before we officially declare that neither
    // element has focus.
    debounce((activeState) => (isNull(activeState) ? timer(blurGraceTimeoutInMs) : EMPTY)),
    // De-dupe emissions that happen as a side-effect of element changes.
    distinctUntilChanged(),
  );
}

/** Emits when the user interacts with the editor. */
function fromEditorInteractionState(
  editorElement$: Observable<HTMLElement | null>,
): Observable<FormattingPopoverInteractionStatesEnum | undefined> {
  return editorElement$.pipe(
    switchMap((element) => {
      if (!element) {
        return EMPTY;
      }

      return merge(fromEditorMouseInteractionState(element), fromEditorKeyboardInteractionState(element));
    }),
  );
}

/** Emits when the user starts and stops interacting with the editor using their mouse. */
function fromEditorMouseInteractionState(
  element: Element,
): Observable<FormattingPopoverInteractionStatesEnum | undefined> {
  return fromIsMouseDown(element).pipe(
    // When the mouse button is depressed, wait until the first mouse move before we declare a drag.
    debounce((isMouseDown) => (isMouseDown ? fromEvent(element, 'mousemove') : EMPTY)),
    map((isMouseDragging) => {
      if (isMouseDragging) {
        return FormattingPopoverInteractionStatesEnum.EDITOR_SELECTING_WITH_MOUSE;
      }

      // Either a drag ended, or the user interacted using the mouse in some other way.
      return undefined;
    }),
  );
}

/** Emits when the user starts and stops interacting with the editor using their keyboard. */
function fromEditorKeyboardInteractionState(element: Element) {
  const firstMouseMoveEvent$ = fromEvent(element, 'mousemove').pipe(first());

  return fromEvent<KeyboardEvent>(element, 'keydown').pipe(
    map((event) => {
      if (isSelectionKeyboardEvent(event)) {
        return FormattingPopoverInteractionStatesEnum.EDITOR_SELECTING_WITH_KEYBOARD;
      }

      if (!isShortcutKeyboardEvent(event)) {
        return FormattingPopoverInteractionStatesEnum.EDITOR_TYPING;
      }

      // The user is interacting with the editor using the keyboard in some other way.
      return undefined;
    }),
    switchMap((state) => {
      const state$ = of(state);

      // If this is not a specific keyboard interaction state, just emit that.
      if (isUndefined(state)) {
        return state$;
      }

      // Clear this keyboard interaction state as soon as the mouse moves.
      return merge(state$, firstMouseMoveEvent$.pipe(mapTo(undefined)));
    }),
  );
}

/** Emits when the user interacts with the popover. */
function fromPopoverInteractionState(
  popoverContentElement$: Observable<HTMLElement | null>,
): Observable<FormattingPopoverInteractionStatesEnum | undefined> {
  return popoverContentElement$.pipe(
    switchMap((element) =>
      fromPopoverMouseInteractionState(element).pipe(
        // Clear any previous state when the element changes.
        startWith(undefined),
      ),
    ),
    // Skip the initial emit, because there is no previous state to clear for the initial element.
    skip(1),
  );
}

/** Emits when the user starts and stops interacting with the popover using their mouse. */
function fromPopoverMouseInteractionState(
  element: Element | null,
): Observable<FormattingPopoverInteractionStatesEnum | undefined> {
  return fromIsMouseOver(element).pipe(
    // We may get a mouseenter event simply because the mouse happens to be over the popover when it appears, so wait
    // for at least one mousemove before we declare an intentional hover.
    debounce((isMouseOver) => (element && isMouseOver ? fromEvent(element, 'mousemove') : EMPTY)),
    map((isMouseOverAndDidMouseMove) => {
      if (isMouseOverAndDidMouseMove) {
        return FormattingPopoverInteractionStatesEnum.POPOVER_HOVERING;
      }

      return undefined;
    }),
  );
}

/*
 * DOM helpers.
 */

/** Emits whether the given element is focused or contains focus, when subscribed to and when it changes. */
function fromIsActive(element: Element | null): Observable<boolean> {
  if (!element) {
    return of(false);
  }

  return merge(of(undefined), fromEvent(element, 'focusin'), fromEvent(element, 'focusout')).pipe(
    map(() => {
      // Don't trust TS types and assume ownerDocument may be null.
      const activeElement = element.ownerDocument?.activeElement;
      return activeElement ? element.contains(activeElement) : false;
    }),
    // De-dupe events bubbling up from multiple children.
    distinctUntilChanged(),
  );
}

/** Emits when the mouse enters and leaves the given element. */
function fromIsMouseOver(element: Element | null): Observable<boolean> {
  if (!element) {
    return EMPTY;
  }

  return merge(
    fromEvent(element, 'mouseenter').pipe(mapTo(true)),
    fromEvent(element, 'mouseleave').pipe(mapTo(false)),
  );
}

/** Emits when a mouse button is depressed or released on the given element. */
function fromIsMouseDown(element: Element): Observable<boolean> {
  return merge(
    fromEvent(element, 'mousedown').pipe(mapTo(true)),
    fromEvent(element, 'mouseup').pipe(mapTo(false)),
    fromEvent(element, 'mouseleave').pipe(mapTo(false)),
  ).pipe(
    // De-dupe mouseup and mouseleave happening in sequence.
    distinctUntilChanged(),
  );
}

const arrowKeys = new Set<string>([Key.ArrowUp, Key.ArrowDown, Key.ArrowLeft, Key.ArrowRight]);

function isSelectionKeyboardEvent(event: KeyboardEvent): boolean {
  return arrowKeys.has(event.key);
}

function isShortcutKeyboardEvent(event: KeyboardEvent): boolean {
  return event.ctrlKey || event.metaKey;
}
